import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "row" }
const _hoisted_2 = { class: "col" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("hr", null, null, -1)
const _hoisted_4 = { class: "row row-cols-1 row-cols-md-2" }
const _hoisted_5 = {
  key: 0,
  class: "row"
}

import { CACHE_TTL__ONE_MONTH } from '@/services/CacheService'
import { computed, onBeforeMount, reactive, ref, watch } from 'vue'
import { cacheService } from '@/di/services'
import { FormKitSelectOption } from '@/types/app.tmp'
import { silpoProductsApi } from '@/di/api'
import { useStore } from "vuex";
import AppLoadingMessage from "@/components/app/AppLoadingMessage.vue";
import { City, ProductInfo, Store, Uuid } from "@/types/api";
import SilpoProductInfo from "@/components/shopping/silpo/SilpoProductInfo.vue";
import FormKitSubmitButtonContent from "@/components/app/FormKit/FormKitSubmitButtonContent.vue";

interface Model {
  cityId: Uuid | null,
  storeId: number | null,
  term: string,
  pageNumber: number,
}

interface Data {
  productInfos: ProductInfo[],
  hasNext: boolean,
}

interface Lock {
  preparing: boolean,
  productsListLoading: boolean,
}

type FormKitSelectOptionWithId = FormKitSelectOption & { id: number }


export default /*#__PURE__*/_defineComponent({
  __name: 'SilpoProductsSearch',
  setup(__props) {

const CACHE_KEY__CURRENT_CITY = '01G4DS9BZW8PA76XASHY1PQEYA'
const CACHE_KEY__CURRENT_STORE = '01G4DS9Q3YGC097AD2V1YTCY2M'

const store = useStore()

const model = ref<Model>({ cityId: null, storeId: null, term: '', pageNumber: 1 })
const data = reactive<Data>({ productInfos: [], hasNext: true })
const lock = reactive<Lock>({ preparing: false, productsListLoading: false })

const cities = computed<City[]>((): City[] => store.getters['shopping/silpo/cities/list'])
const stores = computed<Store[]>((): Store[] => store.getters['shopping/silpo/stores/list'])

const cityOptions = computed<FormKitSelectOption[]>((): FormKitSelectOption[] => cities.value.map((c) => ({
  value: c.id,
  label: c.name
})))

const storeOptions = computed<FormKitSelectOptionWithId[]>((): FormKitSelectOptionWithId[] => {
  const city = cities.value.filter(c => c.id === model.value.cityId)[0]

  if (!city) {
    return []
  }

  return stores.value
      .filter(s => s.city_id === city.city_id)
      .map(s => ({ id: s.filial_id, value: s.filial_id, label: s.title, attrs: { disabled: !s.active } }))
})

const query = computed(() => ({
  filter: {
    filial_id: parseInt('' + model.value.storeId)
  },
  query: {
    name: model.value.term
  },
  paging: {
    page: model.value.pageNumber,
    per_page: 10
  }
}))

watch(() => model.value.cityId, (value): void => {
  cacheService.set(CACHE_KEY__CURRENT_CITY, value, CACHE_TTL__ONE_MONTH)
})

watch(() => model.value.storeId, (value): void => {
  cacheService.set(CACHE_KEY__CURRENT_STORE, value, CACHE_TTL__ONE_MONTH)
})

function onCityIdChange () {
  model.value.storeId = storeOptions.value[0]?.id
}

async function handleSearch (): Promise<void> {
  model.value.pageNumber = 1
  data.productInfos = []

  await loadProducts()
}

async function loadProducts (): Promise<void> {
  lock.productsListLoading = true

  try {
    const productInfos = (await silpoProductsApi.searchProductInfos(query.value)).items

    if (productInfos.length > 0) {
      data.productInfos.push(...productInfos)
      model.value.pageNumber++
    }

    if (
        productInfos.length < query.value.paging.per_page
        || productInfos.length === 0
    ) {
      data.hasNext = false
    }
  } finally {
    lock.productsListLoading = false
  }
}

onBeforeMount(async (): Promise<void> => {
  lock.preparing = true

  try {
    await store.dispatch('shopping/silpo/cities/init')
    await store.dispatch('shopping/silpo/stores/init')
  } finally {
    lock.preparing = false
  }

  model.value.cityId = cacheService.get(CACHE_KEY__CURRENT_CITY)
  model.value.storeId = cacheService.get(CACHE_KEY__CURRENT_STORE)
})

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        (lock.preparing)
          ? (_openBlock(), _createBlock(AppLoadingMessage, { key: 0 }, {
              default: _withCtx(() => [
                _createTextVNode("Preparing")
              ]),
              _: 1
            }))
          : (_openBlock(), _createBlock(_component_FormKit, {
              key: 1,
              modelValue: model.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((model).value = $event)),
              type: "form",
              "form-class": "row row-cols-lg-auto g-3 align-items-center",
              actions: false,
              onSubmit: handleSearch
            }, {
              default: _withCtx(() => [
                _createVNode(_component_FormKit, {
                  name: "cityId",
                  type: "select",
                  label: false,
                  placeholder: "Select city",
                  options: _unref(cityOptions),
                  "wrapper-class": "mx-sm-1",
                  disabled: lock.productsListLoading,
                  "input-class": "form-control form-control-sm",
                  onChange: onCityIdChange
                }, null, 8, ["options", "disabled"]),
                _createVNode(_component_FormKit, {
                  name: "storeId",
                  type: "select",
                  label: false,
                  disabled: !model.value.cityId || lock.productsListLoading,
                  placeholder: "Select the store",
                  options: _unref(storeOptions),
                  "wrapper-class": "mx-sm-1",
                  "input-class": "form-control form-control-sm"
                }, null, 8, ["disabled", "options"]),
                _createVNode(_component_FormKit, {
                  name: "term",
                  type: "text",
                  label: false,
                  disabled: !model.value.cityId || !model.value.storeId || lock.productsListLoading,
                  placeholder: "Product name",
                  "wrapper-class": "mx-sm-1",
                  "input-class": "form-control form-control-sm"
                }, null, 8, ["disabled"]),
                _createVNode(_component_FormKit, {
                  type: "submit",
                  disabled: !model.value.cityId || !model.value.storeId || !model.value.term || lock.productsListLoading,
                  "input-class": "btn btn-primary btn-sm"
                }, {
                  default: _withCtx(() => [
                    _createVNode(FormKitSubmitButtonContent, null, {
                      default: _withCtx(() => [
                        _createTextVNode("Search")
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["disabled"])
              ]),
              _: 1
            }, 8, ["modelValue"]))
      ])
    ]),
    _hoisted_3,
    _createElementVNode("div", _hoisted_4, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(data.productInfos, (productInfo) => {
        return (_openBlock(), _createElementBlock("div", {
          key: productInfo.id,
          class: "col mb-4"
        }, [
          _createVNode(SilpoProductInfo, { "product-info": productInfo }, null, 8, ["product-info"])
        ]))
      }), 128)),
      (lock.productsListLoading)
        ? (_openBlock(), _createBlock(AppLoadingMessage, {
            key: 0,
            class: "mb-3"
          }, {
            default: _withCtx(() => [
              _createTextVNode("Searching products")
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    (!lock.productsListLoading && data.hasNext && data.productInfos.length > (_unref(query).paging.per_page - 1))
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createElementVNode("div", { class: "col" }, [
            _createElementVNode("button", {
              class: "btn btn-sm btn-primary",
              onClick: loadProducts
            }, "Load more")
          ])
        ]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})